import { useEffect, useState } from 'preact/hooks';
import type { PaymentOption, PublicSession } from '../api/types';
import { CHAINS, explorerTxUrl } from '../chains';
import { t } from '../i18n';
import { isMobile, shortHash } from '../utils/format';
import { deepLinksFor, onWalletsChanged, pay, walletsFor, WalletError, type PayProgress, type WalletDescriptor } from '../wallets';

interface Props {
  session: PublicSession;
  option: PaymentOption;
}

type State =
  | { kind: 'idle' }
  | { kind: 'busy'; wallet: WalletDescriptor; stage: PayProgress }
  | { kind: 'sent'; wallet: WalletDescriptor; txHash: string }
  | { kind: 'error'; wallet: WalletDescriptor; message: string };

export function WalletButtons({ option }: Props) {
  const [, bump] = useState(0);
  const [state, setState] = useState<State>({ kind: 'idle' });
  useEffect(() => onWalletsChanged(() => bump((n) => n + 1)), []);
  useEffect(() => setState({ kind: 'idle' }), [option.chain, option.asset]);

  const kind = CHAINS[option.chain].kind;
  if (kind === 'utxo') return null; // BTC: QR + URI only

  const wallets = walletsFor(option);
  const mobile = isMobile();
  const deepLinks = mobile ? deepLinksFor(option) : [];
  const busy = state.kind === 'busy';

  const run = async (w: WalletDescriptor) => {
    if (!w.available) {
      if (mobile) {
        const dl = deepLinks.find((d) => d.name.toLowerCase().includes(w.name.toLowerCase().split(' ')[0]));
        if (dl) {
          location.href = dl.href;
          return;
        }
      }
      if (w.installUrl) window.open(w.installUrl, '_blank', 'noopener');
      return;
    }
    setState({ kind: 'busy', wallet: w, stage: 'connecting' });
    try {
      const res = await pay(w, { option, onProgress: (stage) => setState({ kind: 'busy', wallet: w, stage }) });
      setState({ kind: 'sent', wallet: w, txHash: res.txHash });
    } catch (e) {
      const err = e instanceof WalletError ? e : new WalletError('failed', String(e));
      const message =
        err.code === 'rejected'
          ? t('wallet.rejected')
          : err.code === 'not_installed'
            ? t('wallet.notInstalled', { wallet: w.name })
            : err.code === 'misconfigured'
              ? t('wallet.wcMissing')
              : err.code === 'insufficient'
                ? t('wallet.insufficient', { asset: option.symbol })
                : t('wallet.error', { message: err.message });
      setState({ kind: 'error', wallet: w, message });
    }
  };

  return (
    <section aria-labelledby="cv-wallet-title">
      <h2 id="cv-wallet-title" class="cv-section-title">
        {t('wallet.title')}
      </h2>
      <p class="cv-hint" style={{ marginBottom: 10 }}>
        {t('wallet.subtitle')}
      </p>

      {state.kind === 'busy' && (
        <div class="cv-wallet-status" role="status" aria-live="polite">
          <div class="cv-spinner" aria-hidden="true" />
          <span>
            {state.wallet.name}:{' '}
            {state.stage === 'connecting'
              ? t('wallet.connecting')
              : state.stage === 'switching'
                ? t('wallet.switching')
                : state.stage === 'confirm'
                  ? t('wallet.confirm')
                  : t('wallet.sent')}
          </span>
        </div>
      )}
      {state.kind === 'sent' && (
        <div class="cv-wallet-status" role="status" aria-live="polite">
          <span>
            {t('wallet.sent')} ·{' '}
            <a class="mono" href={explorerTxUrl(option.chain, state.txHash)} target="_blank" rel="noopener noreferrer">
              {shortHash(state.txHash)}
            </a>
          </span>
        </div>
      )}
      {state.kind === 'error' && (
        <div class="cv-wallet-status error" role="alert">
          {state.message}
        </div>
      )}

      <div class="cv-wallets" style={{ marginTop: 10 }}>
        {wallets.map((w) => (
          <button
            key={w.id}
            type="button"
            class={`cv-btn${w.available ? ' primary' : ''}`}
            disabled={busy}
            onClick={() => run(w)}
            title={w.available ? undefined : t('wallet.notInstalled', { wallet: w.name })}
          >
            {w.icon ? (
              <img class="icon" src={w.icon} alt="" />
            ) : (
              <span class="icon" aria-hidden="true" style={{ display: 'grid', placeItems: 'center', fontWeight: 700 }}>
                {w.name[0]}
              </span>
            )}
            {w.available ? w.name : mobile ? t('wallet.openMobile', { wallet: w.name }) : t('wallet.install', { wallet: w.name })}
          </button>
        ))}
      </div>
      {wallets.length === 0 && <p class="cv-hint">{t('wallet.noWallets')}</p>}
    </section>
  );
}
